import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { getTokenExpiryMs } from './jwt.utils';

const ACCESS_TOKEN_KEY = 'AccessToken';
const SESSION_WARNING_MS = 5 * 60 * 1000;
/** setTimeout's own max delay (2^31 - 1 ms, ~24.8 days) — admin tokens are only
 * valid for a few hours, but timers are chained past this in case that ever changes. */
const MAX_TIMEOUT_MS = 2147483647;

interface TimerHandle {
  id: ReturnType<typeof setTimeout> | null;
}

/**
 * Schedules a "session expiring soon" toast and an automatic logout at the exact
 * moment the stored JWT's `exp` claim is reached, driven off the token itself
 * rather than an idle/inactivity guess. Started on login and on app bootstrap
 * (so a page refresh keeps the same schedule); stopped on manual logout.
 */
@Injectable({ providedIn: 'root' })
export class SessionTimerService {
  private readonly warningHandle: TimerHandle = { id: null };
  private readonly logoutHandle: TimerHandle = { id: null };
  private warningToastRef: ActiveToast<unknown> | null = null;

  constructor(
    private readonly router: Router,
    private readonly toaster: ToastrService,
    private readonly dialogRef: MatDialog,
  ) {
    window.addEventListener('storage', (event: StorageEvent) => this.onStorageChange(event));
  }

  start(): void {
    this.stop();

    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    const expiryMs = token ? getTokenExpiryMs(token) : null;
    if (!expiryMs) return;

    const msToExpiry = expiryMs - Date.now();
    if (msToExpiry <= 0) {
      this.expireNow();
      return;
    }

    const msToWarning = msToExpiry - SESSION_WARNING_MS;
    if (msToWarning > 0) {
      this.armTimer(this.warningHandle, msToWarning, () => this.showWarning());
    } else {
      this.showWarning();
    }
    this.armTimer(this.logoutHandle, msToExpiry, () => this.expireNow());
  }

  stop(): void {
    this.clearTimer(this.warningHandle);
    this.clearTimer(this.logoutHandle);
    this.dismissWarningToast();
  }

  private armTimer(handle: TimerHandle, delayMs: number, onFire: () => void): void {
    const clampedDelay = Math.min(Math.max(delayMs, 0), MAX_TIMEOUT_MS);
    handle.id = setTimeout(() => {
      if (delayMs > MAX_TIMEOUT_MS) {
        this.armTimer(handle, delayMs - MAX_TIMEOUT_MS, onFire);
      } else {
        onFire();
      }
    }, clampedDelay);
  }

  private clearTimer(handle: TimerHandle): void {
    if (handle.id === null) return;
    clearTimeout(handle.id);
    handle.id = null;
  }

  private showWarning(): void {
    this.warningToastRef = this.toaster.warning(
      'Your session will expire soon. Please save your work.',
      'Session expiring',
      { disableTimeOut: true, tapToDismiss: true, closeButton: true },
    );
  }

  private dismissWarningToast(): void {
    if (!this.warningToastRef) return;
    this.toaster.remove(this.warningToastRef.toastId);
    this.warningToastRef = null;
  }

  private redirectToLoginAsExpired(): void {
    this.dialogRef.closeAll();
    this.toaster.info('You have been logged out due to session expiry.');
    this.router.navigate(['/login'], { queryParams: { sessionExpired: 'true' } });
  }

  private expireNow(): void {
    this.stop();
    localStorage.clear();
    this.redirectToLoginAsExpired();
  }

  private onStorageChange(event: StorageEvent): void {
    if (event.key !== ACCESS_TOKEN_KEY) return;

    if (!event.newValue) {
      this.stop();
      if (!this.router.url.startsWith('/login')) {
        this.redirectToLoginAsExpired();
      }
      return;
    }
    this.start();
  }
}
